import { z } from "zod";

import { isPermissionOrWildcard } from "@/features/rbac/permissions/catalog";
import type { PermissionOrWildcard } from "@/features/rbac/permissions/catalog";

/**
 * Identificador de rol: `lower_snake_case`, sin espacios ni acentos. Se deriva
 * del nombre al crear el rol y luego es inmutable, porque las asignaciones
 * apuntan a él.
 */
export const ROLE_ID_PATTERN = /^[a-z][a-z0-9_]{1,49}$/;

const roleIdSchema = z
  .string()
  .regex(ROLE_ID_PATTERN, "El identificador del rol no tiene un formato válido");

/**
 * Lista de permisos tal como llega de Firestore.
 *
 * Los valores desconocidos se DESCARTAN en vez de rechazar el documento
 * completo: si un despliegue anterior escribió un permiso que este build ya no
 * conoce (o al revés), el rol sigue siendo utilizable en lugar de romper la
 * sesión entera. El resultado se deduplica y se ordena para que las escrituras
 * sean estables.
 */
const storedPermissionsSchema = z
  .array(z.unknown())
  .catch([])
  .transform((values) => {
    const kept = values.filter(
      (value): value is PermissionOrWildcard =>
        typeof value === "string" && isPermissionOrWildcard(value)
    );
    return [...new Set(kept)].sort();
  });

/** Permisos aceptados al escribir: aquí sí se rechaza un valor inválido. */
const writablePermissionsSchema = z
  .array(
    z.string().refine(isPermissionOrWildcard, {
      message: "El permiso no existe en el catálogo",
    })
  )
  .transform((values) => [...new Set(values)].sort() as PermissionOrWildcard[]);

/** Documento `rbac_roles/{roleId}` ya normalizado. */
export const rbacRoleSchema = z.object({
  id: roleIdSchema,
  name: z.string().min(1, "El nombre del rol es obligatorio").max(80),
  description: z.string().max(500).default(""),
  permissions: storedPermissionsSchema,
  system: z.boolean().default(false),
  legacyKey: z.string().nullable().default(null),
  updatedAt: z.date().nullable().default(null),
  updatedBy: z.string().nullable().default(null),
});

export type RbacRole = z.infer<typeof rbacRoleSchema>;

export const createRoleSchema = z.object({
  name: z.string().trim().min(1, "El nombre del rol es obligatorio").max(80),
  description: z.string().trim().max(500).default(""),
  permissions: writablePermissionsSchema,
});

export type CreateRoleInput = z.input<typeof createRoleSchema>;

export const updateRoleSchema = z.object({
  name: z.string().trim().min(1, "El nombre del rol es obligatorio").max(80).optional(),
  description: z.string().trim().max(500).optional(),
  permissions: writablePermissionsSchema.optional(),
});

export type UpdateRoleInput = z.input<typeof updateRoleSchema>;

/** De dónde viene la identidad del sujeto al que se le asignan roles. */
export const subjectTypeSchema = z.enum(["user", "staff"]);

export type SubjectType = z.infer<typeof subjectTypeSchema>;

/** Documento `rbac_assignments/{subjectUid}` ya normalizado. */
export const rbacAssignmentSchema = z.object({
  subjectUid: z.string().min(1),
  subjectType: subjectTypeSchema.catch("user"),
  subjectEmail: z.string().nullable().default(null),
  roleIds: z
    .array(z.unknown())
    .catch([])
    .transform((values) => {
      const kept = values.filter(
        (value): value is string => typeof value === "string" && value.length > 0
      );
      return [...new Set(kept)].sort();
    }),
  updatedAt: z.date().nullable().default(null),
  updatedBy: z.string().nullable().default(null),
});

export type RbacAssignment = z.infer<typeof rbacAssignmentSchema>;
